import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useAccountStorage from '../hooks/useAccountStorage';
import useFeatureFlag, { STORAGE_GRACE_FLAG } from '../hooks/useFeatureFlag';
import StorageMeter from './StorageMeter';
import { createPortalSession } from '../firebase';
import { poolBytesFromGb } from '../photoUpload/caps';
import {
  getTierById,
  splitCreditBuckets,
  formatPeriodDate,
  hasBillingAccount,
} from '../pricing/subscription';
import { trackEvent } from '../analytics';
import { rememberPortalTier } from '../upsell/attribution';
import { UPSELL_ACTIONS, UPSELL_PROMPTS } from '../upsell/triggers';

// The in-app home for a subscriber's relationship with Tweedia (#349): what
// tier am I on, how much storage and credit is left, and how do I change it.
//
// The last part is why this exists at all — create_portal_session was deployed
// but called from nowhere, so a subscriber had no way to cancel, switch plans,
// or update a card, while the re-subscribe error told them to "use the billing
// portal".
//
// The account doc arrives as props: AccountSettingsPage already listens to
// users/{uid}, so opening another listener here would be a second socket on
// the same document.
function PlanBillingSection({
  user, entitlements, subscription, credits, storageGrace, loading, error,
}) {
  const storage = useAccountStorage(user);
  // Client half of the #346 kill switch. The server gates already refuse to
  // freeze, stamp or sweep while this is off; without it here, an account that
  // was stamped before the switch was pulled would still be told we're about
  // to delete its photos — the one claim the switch exists to retract.
  // Defaults OFF and stays off while the flag doc is loading, so the banner
  // never flashes a deletion date it then takes back.
  const { enabled: graceFeatureEnabled } = useFeatureFlag(STORAGE_GRACE_FLAG, {
    defaultValue: false,
  });
  const [portalPending, setPortalPending] = useState(false);
  const [portalError, setPortalError] = useState(null);

  if (loading || !entitlements) {
    return (
      <section className="settings-section" aria-labelledby="plan-heading">
        <h2 id="plan-heading">Plan &amp; Billing</h2>
        <p className="settings-muted" data-testid="plan-loading">Loading your plan…</p>
      </section>
    );
  }

  const tier = getTierById(entitlements.tier);
  const tierName = tier ? tier.name : 'Free';
  const isFree = entitlements.tier === 'free';
  const billingAccount = hasBillingAccount(subscription);
  const { allotment, topUp } = splitCreditBuckets(credits);

  const renewalDate = formatPeriodDate(subscription && subscription.currentPeriodEnd);
  const cancelling = Boolean(subscription && subscription.cancelAtPeriodEnd);

  const poolBytes = poolBytesFromGb(entitlements.storageGb);
  const usedBytes = storage.loading ? null : storage.usedBytes;

  // #346 downgrade lifecycle. The stamp exists only while the server considers
  // the account over its allowance, and it carries the deadline the nightly
  // sweep reclaims after. Rendered off `sweepAfter` rather than the map's mere
  // presence: a banner that cannot name a date has nothing useful to say.
  const sweepAfter = Number(storageGrace && storageGrace.sweepAfter);
  const sweepDate = formatPeriodDate(sweepAfter);

  // …but the stamp OUTLIVES the condition it describes. The server gates are
  // keyed on the LIVE overage (`is_storage_frozen` is literally `is_over_pool`),
  // so a customer who deletes a project is unfrozen instantly, while
  // `storageGrace` survives until a Stripe event or the 03:00 UTC reconcile
  // removes it. Showing the banner off the stamp alone would spend up to a day
  // telling them they can't upload or render — and naming a date we'll start
  // removing photos — while the app happily lets them do both.
  //
  // Gating on the live numbers too also keeps it hidden while the meter is
  // loading (`usedBytes` is null then) rather than flashing a deletion warning.
  const overPool = usedBytes != null && usedBytes > poolBytes;
  const showGraceBanner = graceFeatureEnabled && Boolean(sweepDate) && overPool;

  const monthlyAllowance = Number(entitlements.monthlyCredits) || 0;
  // allotmentPeriodStart is written only once the allotment engine (#340) has
  // granted a cycle. Absent it, `allotment` is 0 because nothing has issued
  // credits yet — not because they were spent — and metering that as
  // "0 of 75 left" tells a healthy subscriber they are out.
  const allotmentIssued = Boolean(credits && credits.allotmentPeriodStart != null);
  const allotmentPercent = monthlyAllowance > 0
    ? Math.min(100, Math.round((allotment / monthlyAllowance) * 100))
    : 0;
  const allotmentExpiry = formatPeriodDate(credits && credits.allotmentPeriodEnd);

  const openPortal = async () => {
    trackEvent('billing_portal_opened', { tier: entitlements.tier });
    // A plan change made in the portal comes back through return_url as a full
    // page load, so useSubscriptionFunnelEvents has no previous snapshot to
    // diff and the tier move would go unreported. Stash the before-tier now —
    // it is the only moment the old value is still known. (#350)
    rememberPortalTier(entitlements.tier);
    setPortalError(null);
    setPortalPending(true);
    try {
      const { data } = await createPortalSession({
        return_url: `${window.location.origin}/account`,
      });
      if (data && data.url) {
        window.location.assign(data.url);
        return;
      }
      throw new Error('No portal URL returned.');
    } catch (err) {
      console.error('PlanBillingSection: portal session failed', err);
      setPortalError(
        (err && err.message) || 'We couldn’t open the billing portal. Please try again.',
      );
      setPortalPending(false);
    }
  };

  return (
    <section className="settings-section" aria-labelledby="plan-heading" data-testid="plan-billing">
      <h2 id="plan-heading">Plan &amp; Billing</h2>

      {error && (
        <p className="plan-error" role="alert" data-testid="plan-load-error">
          We couldn’t load your current plan, so this shows the free defaults.
          Please refresh to try again.
        </p>
      )}

      <div className="plan-summary">
        <div className="plan-summary__tier">
          <span className="plan-summary__name" data-testid="plan-tier-name">{tierName}</span>
          {tier && tier.founding && (
            <span className="plan-founding-badge" data-testid="plan-founding-badge">Founding</span>
          )}
        </div>
        {!isFree && renewalDate && (
          <p className="plan-summary__renewal" data-testid="plan-renewal">
            {cancelling
              ? `Cancels on ${renewalDate} — you keep everything until then.`
              : `Renews on ${renewalDate}.`}
          </p>
        )}
        {isFree && (
          <p className="plan-summary__renewal" data-testid="plan-renewal">
            {billingAccount
              ? 'Your subscription has ended — you’re on the free plan.'
              : 'You’re on the free plan.'}
          </p>
        )}
      </div>

      {/* Over-allowance banner (#346). Sits directly above the meter it is
          about, and says the three things a frozen customer needs in one read:
          nothing has been deleted, what they can't do until they fix it, and
          the date after which we start freeing space for them. */}
      {showGraceBanner && (
        <div className="plan-grace" role="status" data-testid="storage-grace-banner">
          <p className="plan-grace__head">
            You’re over your plan’s storage limit.
          </p>
          <p className="plan-grace__body">
            Nothing has been deleted — everything you’ve made is still here to
            view, download and export. Until you’re back under the limit you
            can’t upload new photos or create new videos.
          </p>
          <p className="plan-grace__body">
            On <strong data-testid="storage-grace-date">{sweepDate}</strong> we’ll
            start removing the raw source photos from your least recently used
            projects until your account fits. Free up space or move to a bigger
            plan before then and nothing is removed.
          </p>
          {/* The remedy that needs a link. "Free up space" is done in the app
              they are already in; a bigger plan is not. Routed through the same
              upsell event the other #350 touchpoints emit so this one shows up
              in the same funnel. */}
          <Link
            to="/pricing"
            className="btn btn-teal plan-grace__cta"
            data-testid="storage-grace-upgrade"
            onClick={() => trackEvent('upsell_prompt_clicked', {
              prompt_id: UPSELL_PROMPTS.STORAGE_POOL_FULL,
              from_tier: entitlements.tier,
              action: UPSELL_ACTIONS.VIEW_PLANS,
            })}
          >
            See bigger plans
          </Link>
        </div>
      )}

      {/* Shared with the storage-full upsell prompt (#350) so the modal shows
          the same meter rather than a second rendering of the same numbers.
          The hint copy is StorageMeter's default so both surfaces describe the
          pool identically — photos + source videos + renders since #342/#412. */}
      <StorageMeter
        usedBytes={usedBytes}
        totalBytes={poolBytes}
        label="Storage"
      />

      <div className="plan-meter" data-testid="credit-meter">
        <div className="plan-meter__head">
          <span className="plan-meter__label">Monthly credits</span>
          <span className="plan-meter__value" data-testid="allotment-value">
            {monthlyAllowance <= 0 && 'None on this plan'}
            {monthlyAllowance > 0 && allotmentIssued && `${allotment} of ${monthlyAllowance} left`}
            {monthlyAllowance > 0 && !allotmentIssued && `${monthlyAllowance} per month`}
          </span>
        </div>
        {monthlyAllowance > 0 && allotmentIssued && (
          <div className="plan-meter__track">
            <div className="plan-meter__fill" style={{ width: `${allotmentPercent}%` }} />
          </div>
        )}
        <p className="plan-meter__hint">
          {monthlyAllowance <= 0 && 'Monthly credits come with Solo and Pro.'}
          {monthlyAllowance > 0 && !allotmentIssued
            && 'Issued at the start of each billing cycle. Unused monthly credits don’t roll over.'}
          {monthlyAllowance > 0 && allotmentIssued && (allotmentExpiry
            ? `Unused monthly credits expire on ${allotmentExpiry}.`
            : 'Unused monthly credits don’t roll over.')}
        </p>
      </div>

      <div className="plan-stat-row" data-testid="topup-balance">
        <span className="plan-stat-row__label">Top-up credits</span>
        <span className="plan-stat-row__value">{topUp}</span>
      </div>
      <p className="plan-meter__hint">
        Purchased credits — these never expire and are used only after your
        monthly credits run out.
      </p>

      {portalError && (
        <p className="plan-error" role="alert" data-testid="portal-error">{portalError}</p>
      )}

      {/* Gate the portal on the Stripe relationship, not the tier. A lapsed or
          cancelled account reads as tier `free` but still has a customer
          record — and is precisely who needs the portal, to fix the card that
          just failed or pull a final invoice. */}
      <div className="plan-actions">
        {billingAccount && (
          <button
            type="button"
            className="btn btn-teal"
            onClick={openPortal}
            disabled={portalPending}
            data-testid="manage-billing"
          >
            {portalPending ? 'Opening…' : 'Manage billing'}
          </button>
        )}
        {isFree && (
          <Link
            to="/pricing"
            className={billingAccount ? 'btn btn-plan-secondary' : 'btn btn-teal'}
            data-testid="plan-upgrade"
          >
            View plans
          </Link>
        )}
      </div>
      {billingAccount && (
        <p className="plan-meter__hint">
          Change plan, update your card, or cancel in the Stripe billing portal.
        </p>
      )}
    </section>
  );
}

export default PlanBillingSection;
